import {
  Box,
  BoxProps,
  Button,
  ButtonGroup,
  Center,
  CircularProgress,
  Divider,
  FormControl,
  FormErrorMessage,
  FormLabel,
  HStack,
  Heading,
  Input,
  List,
  ListIcon,
  ListItem,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Select,
  SimpleGrid,
  Skeleton,
  Stack,
  Text,
  VStack,
  useBreakpointValue,
  useDisclosure,
  useToast,
  IconButton,
} from '@chakra-ui/react';
import { CloseIcon } from '@chakra-ui/icons';
import { useUserContext } from '@providers/useUserContext';
import { useQueryClient } from '@tanstack/react-query';
import FloatingLabelInput from 'components/FloatingLabelInput';
import {
  useCrossRegionalAssociations,
  useFetchClientCompleteInfo,
  useFetchMe,
  useListClientServices,
  useListCoaches,
  useListLocations,
  useListPurchases,
  useListSites,
  useListVisits,
} from 'hooks/useApiQueries';
import { CheckCircleIcon } from '@chakra-ui/icons';
import { produce } from 'immer';
import { useEffect, useMemo, useRef, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import ReactInputMask from 'react-input-mask';
import { useLocation, useNavigate } from 'react-router';
import { ApiClientExceptionErrorCode } from 'types/api';
import { ClientService, MindbodyUser } from 'types/client';
import { ApiClientException } from 'utils/apiClientException';
import { HTTP, useCopyToClipboard } from 'utils/http';
import ServicesList from './Services/ServicesList';
import VisitsList from './Visits/VisitsList';

const primaryColor = mondayMindbody?.customization?.color ?? '#ff7f00';

function UserProfile() {
  const location = useLocation();

  useEffect(() => {
    const hash = location.hash;
    if (hash) {
      const id = hash.replace('#', '');
      setTimeout(() => {
        // Ajouter un délai avant de faire défiler
        const element = document.getElementById(id);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
        }
      }, 500); // 500 ms de délai
    }
  }, [location]);

  return (
    <SimpleGrid className="myAccountBlocksContainer" columns={{ base: 1, md: 2, lg: 2, xl: 2 }} spacing={5} pb={5}>
      <Sessions />
      <Credits />
      <Statistics />
      <Profile />
      <EditPassword />
      <PaymentMethod />
      <Purchases />
      {
        mondayMindbody.siteId !== '5745076' &&
          mondayMindbody.siteId !== '5745241' &&
          mondayMindbody.siteId !== '5746279' &&
          mondayMindbody.siteId !== '5745580' &&
          mondayMindbody.siteId !== '5746614' && (
            <Sponsorsip />
          ) /* Pas punch marseille & Dynamo Bordeaux & Riise Bordeaux */
      }
    </SimpleGrid>
  );
}

function Sessions() {
  const navigate = useNavigate();
  const { data: visits, isLoading } = useListVisits() || {};
  const { data: sites } = useListSites();
  const { data: crossRegionalAssociationsData } = useCrossRegionalAssociations();
  const [selectedSiteId, setSelectedSiteId] = useState<string | number | null>(null);

  // Filtrer les sites pour ne garder que ceux qui existent dans crossRegionalAssociations
  const filteredSites = useMemo(() => {
    const crossRegionalSiteIds = new Set(
      crossRegionalAssociationsData?.CrossRegionalClientAssociations.map((assoc) => assoc.SiteId),
    );
    return sites?.filter((site) => crossRegionalSiteIds.has(site.Id));
  }, [sites, crossRegionalAssociationsData]);

  const futureVisits = useMemo(() => {
    if (!visits) return [];
    return visits
      ?.filter((visit) => (selectedSiteId ? visit.SiteId === Number(selectedSiteId) : true))
      ?.filter((visit) => visit.VisitType !== -1)
      ?.filter((visit) => new Date(visit.StartDateTime).getTime() > new Date().getTime())
      .sort((a, b) => new Date(a.StartDateTime).getTime() - new Date(b.StartDateTime).getTime());
  }, [visits, selectedSiteId]);

  const totalVisits = useMemo(() => visits?.length ?? 0, [visits]);

  const handleSiteChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedSiteId(event.target.value);
  };

  return (
    <Box className="accountBox" w="full" sx={{}}>
      <Heading className="accountSubTitle" size="md" sx={{ padding: '1em' }}>
        MES PROCHAINES SESSIONS MONDAY
      </Heading>
      <VStack w="full" alignItems={'flex-start'} sx={{ maxWidth: '250px', paddingLeft: '1em' }}>
        <Select placeholder="Filtrer par marque" sx={{ maxWidth: '250px' }} onChange={handleSiteChange}>
          {filteredSites?.map((site) => (
            <option key={site.Id} value={site.Id}>
              {site.Name}
            </option>
          ))}
        </Select>
      </VStack>
      <Skeleton isLoaded={!isLoading}>
        <VStack w="full">
          <VisitsList visits={futureVisits} />
        </VStack>
        {/* Bouton pour naviguer vers l'historique des visites */}
        <VStack padding="2em 1em">
          {futureVisits.length === 0 && (
            <>
              <Text align="center">
                Vous n'avez pas encore réservé de cours {selectedSiteId ? 'au sein des studios de cette marque' : ''}
              </Text>
              {!selectedSiteId && (
                <Button className="accountButton" variant="contained" onClick={() => navigate('/')}>
                  Réserver une session
                </Button>
              )}
            </>
          )}
          {totalVisits > 0 && (
            <Button
              sx={{ marginTop: '12.5em' }}
              className="accountButton"
              variant="contained"
              onClick={() => navigate('visits')}
            >
              Voir toutes mes sessions
            </Button>
          )}
        </VStack>
      </Skeleton>
    </Box>
  );
}

function Credits() {
  const { data: services, isLoading } = useListClientServices();
  const servicesArray = services ? Object.values(services) : [];
  const isMobile = useBreakpointValue({ base: true, md: false }) || false;

  const navigate = useNavigate();

  const validServices =
    servicesArray
      ?.filter((service: ClientService) => {
        const expirationDate = service.ExpirationDate;
        // Vérifie que 'expirationDate' n'est pas 'null' avant de comparer.
        return expirationDate !== null && new Date(expirationDate) > new Date() && service.Remaining > 0;
      })
      .sort(
        (a: ClientService, b: ClientService) => new Date(b.ActiveDate).getTime() - new Date(a.ActiveDate).getTime(),
      ) || // Trier par date d'activation la plus récente
    [];

  return (
    <Box className={!isMobile ? 'SkeletonServices accountBox' : 'accountBox'} w="full">
      <Heading className="accountSubTitle" size="md" sx={{ padding: '1em' }}>
        MES CRÉDITS MONDAY
      </Heading>
      <Skeleton isLoaded={!isLoading}>
        <VStack w="full">
          <ServicesList services={validServices} />z
        </VStack>
        {/* Bouton pour naviguer vers l'historique des crédits */}
        <VStack padding="2em 1em">
          {validServices.length === 0 ? (
            <>
              <Text>Vous n'avez pas de crédit</Text>
              <Button variant="contained" onClick={() => navigate('/tarifs')} className="accountButton">
                Voir nos tarifs
              </Button>
            </>
          ) : (
            <HStack flexDirection={isMobile ? 'column' : 'row'}>
              <Button
                width={isMobile ? '228px' : 'auto'}
                className="accountButton"
                variant="contained"
                onClick={() => navigate('services')}
              >
                Voir tous mes crédits
              </Button>
              <Button
                width={isMobile ? '228px' : 'auto'}
                className="accountButton"
                variant="contained"
                onClick={() => navigate('/tarifs')}
              >
                Acheter des crédits
              </Button>
            </HStack>
          )}
        </VStack>
      </Skeleton>
    </Box>
  );
}

function Statistics() {
  const { data: visits } = useListVisits() || {};
  const { data: sites } = useListSites();
  const totalVists = useMemo(() => visits?.length ?? 0, [visits]);

  const visitsBySite = useMemo(() => {
    const counts: { [key: string]: number } = {};

    visits?.forEach((visit) => {
      const siteId = String(visit.SiteId);
      counts[siteId] = (counts[siteId] || 0) + 1;
    });

    return counts;
  }, [visits]);

  return (
    <Box className="accountBox" w="full" sx={{}}>
      <Heading className="accountSubTitle" size="md" sx={{ padding: '1em' }}>
        MES STATS MONDAY
      </Heading>
      <Box w="full" sx={{ padding: '1em' }}>
        <Box
          width="100%"
          flexDirection="column"
          alignContent="flex-start"
          justifyContent="flex-start"
          className="accountBigStat"
        >
          <Text fontSize="6xl" fontWeight="bold" my={4} textAlign="left" margin={0} className="accountBigStatNumber">
            {totalVists}
          </Text>
          <Text fontSize="2xl" textAlign="left" className="accountBigStatLabel">
            COURS CHEZ MONDAY
          </Text>
        </Box>
        {Object.entries(visitsBySite || {}).map(([siteId, visitCount]) => {
          const site = sites ? sites.find((site) => site.Id === parseInt(siteId)) : null;
          return (
            <Box key={siteId} mt={4}>
              <HStack width="100%" justifyContent="space-between">
                <Text textAlign="left" fontWeight="bold">
                  {visitCount}
                </Text>
                <Text textTransform="uppercase">cours chez {site ? site.Name : '...'}</Text>
              </HStack>
            </Box>
          );
        })}
        <Divider mt={5} />
        <Text textTransform="uppercase" fontSize="xl" mt={4} fontWeight="semibold">
          Mon studio préféré
        </Text>

        <StatisticsPreferredStudio />
        <StatisticsPreferredCoach />
      </Box>
    </Box>
  );
}

function Profile() {
  const { data: user, isLoading } = useFetchMe();

  const navigate = useNavigate();
  const ShoeSize = user?.CustomClientFields.find((customField) => customField.Name === 'Shoe size')?.Value as number;
  const handleEditClick = () => navigate('edit');

  return (
    <Box className="accountBox" w="full">
      <Heading className="accountSubTitle" size="md" sx={{ padding: '1em' }}>
        MON PROFIL
      </Heading>
      <Skeleton isLoaded={!isLoading}>
        <Box w="full" sx={{ padding: '1em' }}>
          <HStack width="100%" justifyContent="space-between">
            <Text align="left" fontWeight="medium" textTransform="uppercase" className="accountFormLabel">
              Nom
            </Text>
            <Text align="right">
              {user?.FirstName} {user?.LastName}
            </Text>
          </HStack>
          {ShoeSize && (
            <HStack width="100%" justifyContent="space-between">
              <Text align="left" fontWeight="medium" textTransform="uppercase" className="accountFormLabel">
                Pointure
              </Text>
              <Text align="right">{ShoeSize}</Text>
            </HStack>
          )}
          <HStack width="100%" justifyContent="space-between" alignItems="flex-start" mt={4}>
            <Text align="left" fontWeight="medium" textTransform="uppercase" className="accountFormLabel">
              Adresse
            </Text>
            <Box textAlign="right">
              <Text>{user?.AddressLine1}</Text>
              <Text>{user?.AddressLine2}</Text>
              <Text>
                {user?.PostalCode} {user?.City}
              </Text>
            </Box>
          </HStack>
          <HStack width="100%" justifyContent="space-between" mt={4}>
            <Text align="left" textTransform="uppercase" fontWeight="medium" className="accountFormLabel">
              Téléphone
            </Text>
            <Text align="right">{user?.MobilePhone}</Text>
          </HStack>
          <HStack width="100%" justifyContent="space-between" mt={4}>
            <Text align="left" textTransform="uppercase" fontWeight="medium" className="accountFormLabel">
              Email
            </Text>
            <Text align="right">{user?.Email}</Text>
          </HStack>
        </Box>
        <VStack padding="2em 1em">
          <Button variant="contained" mt={4} onClick={handleEditClick} className="accountButton">
            MODIFIER
          </Button>
        </VStack>
      </Skeleton>
    </Box>
  );
}

const useReferralLink = () => {
  const { user } = useUserContext();
  const { pathname } = useLocation();
  return useMemo(() => {
    const { UniqueId, Id } = user!;
    const data = { UniqueId, Id };
    const stringifiedData = JSON.stringify(data);
    const encodedData = btoa(stringifiedData);
    const baseUrl = window.location.href.split(pathname).shift() ?? '';

    if (!baseUrl) return null;
    return `${baseUrl}/account?referral=${encodedData}`;
  }, [user, pathname]);
};

function Sponsorsip() {
  const { t } = useTranslation();
  const copyToClipboard = useCopyToClipboard();
  const referralLink = useReferralLink();

  const affiliateInputRef = useRef<HTMLInputElement>(null);

  // if (!referralLink) return null;
  // return null;
  return (
    <Box className="accountBox" w="full" id="parrainage">
      <Heading className="accountSubTitle" size="md" sx={{ padding: '1em' }}>
        {t('PARRAINAGE')}
      </Heading>
      <Stack w="full" sx={{ padding: '1em' }}>
        <HStack width="100%">
          <Text align="left" fontWeight="medium" textTransform="uppercase">
            {t('Parrainez vos proches')}
          </Text>
        </HStack>

        <Text align="left" fontWeight={'medium'}>
          {t('Un ami parrainé = une session offerte')}
        </Text>
        <Text mb={0} align="left">
          La personne que vous parrainez doit :
        </Text>
        <List mt={0} spacing={3} paddingLeft={4}>
          <ListItem mt={0}>
            <ListIcon as={CheckCircleIcon} color="green.500" />
            Se créer un compte via le lien envoyé
          </ListItem>
          <ListItem>
            <ListIcon as={CheckCircleIcon} color="green.500" />
            Acheter l’offre d’essai à 19€
          </ListItem>
          <ListItem>
            <ListIcon as={CheckCircleIcon} color="green.500" />
            Vous recevrez ensuite automatiquement votre crédit
          </ListItem>
        </List>
        <VStack mt={5}>
          <HStack alignItems="flex-end" w="full">
            <FormControl>
              <FormLabel>{t('Votre lien de parrainage')}</FormLabel>
              <Input ref={affiliateInputRef} readOnly value={referralLink ?? ''} />
            </FormControl>
            <Button onClick={() => copyToClipboard(affiliateInputRef.current!, t('Lien copié'))}>
              {t('Copier le lien')}
            </Button>
          </HStack>
          <ReferralInviteModal />
        </VStack>
      </Stack>
    </Box>
  );
}

interface ReferralFormData {
  email: string;
}

function ReferralInviteModal() {
  const { t } = useTranslation();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { register, handleSubmit, setError, formState, reset } = useForm<ReferralFormData>();
  const referralLink = useReferralLink();

  function onSubmit(data: ReferralFormData) {
    return HTTP.post('/client/sendreferralemail', { ...data, link: referralLink })
      .send()
      .then(onClose)
      .then(() => reset())
      .catch((error: ApiClientException) => {
        setError('root', { message: error.getMessage() });
      });
  }

  return (
    <>
      <Button variant="contained" mt={4} onClick={onOpen} className="accountButton">
        {t('Inviter vos proches')}
      </Button>
      <Modal isOpen={isOpen} onClose={onClose}>
        <ModalOverlay />
        <ModalContent as="form" onSubmit={handleSubmit(onSubmit)}>
          <ModalHeader>
            {t('Inviter vos proches')}
            <IconButton
              aria-label="Fermer"
              icon={<CloseIcon />}
              onClick={onClose}
              position="absolute"
              top="8px"
              right="12px"
              size="sm"
            />
          </ModalHeader>
          <ModalBody>
            <FormControl>
              <FormLabel>{t('Adresse email')}</FormLabel>
              <Input {...register('email', { required: true })} type="email" />
            </FormControl>

            <FormControl isInvalid={!!formState.errors.root}>
              <FormErrorMessage>{formState.errors.root?.message}</FormErrorMessage>
            </FormControl>
          </ModalBody>

          <ModalFooter>
            <ButtonGroup>
              <Button type="submit" isLoading={formState.isSubmitting}>
                {t('Envoyer')}
              </Button>
            </ButtonGroup>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
}

function StatisticsPreferredStudio() {
  const navigate = useNavigate();
  const { data: visits } = useListVisits() || {};
  const { data: studios } = useListLocations();

  // Filtrer les visites par le siteId actuel
  const filteredVisits = useMemo(
    () => visits?.filter((visit) => String(visit.SiteId) === mondayMindbody.siteId),
    [visits],
  );

  const visitsByLocation = useMemo(() => {
    const counts: { [key: string]: number } = {};

    filteredVisits?.forEach((visit) => {
      const locationId = `${visit.LocationId}+${visit.SiteId}`;
      counts[locationId] = (counts[locationId] || 0) + 1;
    });

    return counts;
  }, [filteredVisits]);

  const sortedLocationsByVisitsCount = useMemo(
    () => Object.entries(visitsByLocation || {}).sort((a, b) => b[1] - a[1]),
    [visitsByLocation],
  );
  const mostVisitCount = useMemo(
    () => sortedLocationsByVisitsCount && sortedLocationsByVisitsCount?.[0]?.[1],
    [sortedLocationsByVisitsCount],
  );
  const preferredLocation = useMemo(() => {
    if (sortedLocationsByVisitsCount.length) {
      // Prendre le premier élément du tableau trié, qui est le studio le plus fréquenté
      const [preferredLocationId] = sortedLocationsByVisitsCount?.[0] || [];
      if (preferredLocationId) {
        const [locationId, siteId] = preferredLocationId.split('+');
        // Si un studio préféré existe, afficher ses informations

        const preferredLocation = studios?.find(
          (studio) => String(studio.Id) === locationId && String(studio.SiteID) === siteId,
        );

        if (preferredLocation) {
          return preferredLocation;
        }
      }
    }
  }, [studios, sortedLocationsByVisitsCount]);

  if (preferredLocation) {
    return (
      <HStack key={preferredLocation.Id} width="100%" justifyContent="space-between">
        <Text
          color={primaryColor}
          textTransform="uppercase"
          fontSize="xl"
          mt={0}
          fontWeight="semibold"
          textAlign="left"
        >
          {preferredLocation.Name} ({mostVisitCount})
        </Text>
        <Button
          size="sm"
          textAlign="center"
          minWidth="135px"
          padding="2em 1em"
          variant="contained"
          onClick={() => navigate(`/studio/${preferredLocation.Id}`)}
          className="accountButton"
        >
          voir
          <br />
          le planning
        </Button>
      </HStack>
    );
  }
  // Sinon, on peut retourner null ou un message indiquant qu'aucun studio préféré n'a été trouvé.
  return <Text>Aucun studio préféré trouvé.</Text>;
}

function StatisticsPreferredCoach() {
  const navigate = useNavigate();
  const { data: visits } = useListVisits() || {};
  const { data: coaches } = useListCoaches();

  // Filtrer les visites et entraîneurs par le siteId en cours
  const filteredVisits = useMemo(() => {
    return visits?.filter((visit) => visit.SiteId.toString() === mondayMindbody.siteId);
  }, [visits]);

  const visitsByCoach = useMemo(() => {
    return filteredVisits?.reduce((acc, visit) => {
      const staffId = `${visit?.StaffId?.toString()}+${visit?.SiteId?.toString()}`;
      acc[staffId] = (acc[staffId] || 0) + 1;
      return acc;
    }, {} as { [key: string]: number });
  }, [filteredVisits]);

  const mostSeenCoachId = useMemo(() => {
    return Object.entries(visitsByCoach || {}).reduce<{
      id: string | null;
      count: number;
    }>(
      (maxCoach, [coachId, count]) => {
        return count > maxCoach.count ? { id: coachId, count } : maxCoach;
      },
      { id: null, count: 0 },
    ).id;
  }, [visitsByCoach]);

  const mostSeenCoach = useMemo(() => {
    if (mostSeenCoachId) {
      const [coachId, siteId] = mostSeenCoachId.split('+');
      const coach = coaches?.find((coach) => coach.Id.toString() === coachId && String(coach.SiteId) === siteId);
      return coach;
    }
  }, [coaches, mostSeenCoachId]);

  if (!mostSeenCoach) return null;

  return (
    <>
      <Text textTransform="uppercase" fontSize="xl" mt={4} fontWeight="semibold">
        MON COACH PRÉFÉRÉ
      </Text>
      <HStack width="100%" justifyContent="space-between">
        <Text
          color={primaryColor}
          textTransform="uppercase"
          fontSize="xl"
          mt={0}
          fontWeight="semibold"
          textAlign="left"
        >
          {mostSeenCoach.FirstName}
        </Text>
        <Button
          size="sm"
          textAlign="center"
          minWidth="135px"
          padding="2em 1em"
          variant="contained"
          onClick={() => navigate(`/coach/${mostSeenCoach.Id}`)}
          className="accountButton"
        >
          voir
          <br />
          son planning
        </Button>
      </HStack>
    </>
  );
}

function Purchases() {
  const { data, isLoading } = useListPurchases();
  const filteredPurchases = data?.filter(
    (purchase) =>
      purchase.Description.toLowerCase().includes('gift') || purchase.Description.toLowerCase().includes('cadeau'),
  );
  // Voir si on peut utiliser barcode (purchase sell item)

  return (
    <>
      {filteredPurchases && filteredPurchases.length > 0 ? (
        <Box display={'none'} className="accountBox" w="full" sx={{ padding: '1em' }}>
          <Heading textTransform="uppercase" className="accountSubTitle" size="md">
            Cartes cadeaux offertes
          </Heading>

          {isLoading ? (
            <Center mt={10}>
              <CircularProgress isIndeterminate color="primary.500" />
            </Center>
          ) : (
            <Stack>
              {filteredPurchases.length ? (
                filteredPurchases.slice(0, 5).map((purchase) => (
                  <HStack key={purchase.Sale.Id} borderBottom="1px solid black" py={3}>
                    <Text>{purchase.Description} - </Text>
                    <Text fontWeight="bold">{Math.abs(purchase.AmountPaid)}€</Text>
                  </HStack>
                ))
              ) : (
                <Text>Aucun achats</Text>
              )}
            </Stack>
          )}
        </Box>
      ) : null}
    </>
  );
}

interface PaymentMethodData {
  CreditCardNumber: string;
  BillingName: string;
  Exp: string;
  Cryptogram: string;
}

function PaymentMethod() {
  const { isOpen, onClose, onOpen } = useDisclosure();
  const queryClient = useQueryClient();

  const {
    handleSubmit,
    register,
    setError,
    formState: { errors, isSubmitting },
    reset,
  } = useForm<PaymentMethodData>();
  const { t } = useTranslation();
  const { data: user, isLoading } = useFetchClientCompleteInfo();
  const hasPaymentMethodSetup = !!user?.Client.ClientCreditCard?.CardNumber;

  async function onSubmit(data: PaymentMethodData) {
    try {
      const response = await HTTP.post<MindbodyUser>('/client/paymentinfo', data).send();

      if (user) {
        const update = produce(user, (user) => {
          if (user) {
            user.Client.ClientCreditCard = response.ClientCreditCard;
          }
        });

        queryClient.setQueryData(['/client/completeinfo', user.Client.Id], update);
      }
      onClose();
      reset();
    } catch (error) {
      if (error instanceof ApiClientException) {
        if (error.Message === 'Code: InvalidCardType\nSubCode: \nDetail: \nLevel: Error\n') {
          return setError('root', { message: t("Ce type de carte de crédit n'est pas accepté.") });
        }
        setError('root', { message: error.getMessage() });
      }
      return null;
    }
  }

  return (
    <UserProfileCard title={t('Méthode de paiement')}>
      <Skeleton isLoaded={!isLoading} mt={10}>
        {hasPaymentMethodSetup && !isOpen ? (
          <Stack>
            <Text>
              <b>{t('Nom')}:</b> {user.Client.ClientCreditCard?.CardHolder}
            </Text>
            <Text>
              <b>{t('Numéro')}:</b> {user.Client.ClientCreditCard?.CardNumber}
            </Text>
            <Text>
              <b>{t('Expire le')}:</b> {user.Client.ClientCreditCard?.ExpMonth}/{user.Client.ClientCreditCard?.ExpYear}
            </Text>

            <ButtonGroup mt={5}>
              <Button onClick={onOpen}>{t('Modifier')}</Button>
            </ButtonGroup>
          </Stack>
        ) : (
          <Stack mt={5}>
            <FormControl>
              <FormLabel>{t('Nom du titulaire')}</FormLabel>
              <Input {...register('BillingName', { required: true })} />
            </FormControl>

            <Stack direction={{ base: 'column', md: 'row' }} as="form" onSubmit={handleSubmit(onSubmit)}>
              <FormControl isInvalid={!!errors?.CreditCardNumber}>
                <FormLabel>{t('Numéro de carte')}</FormLabel>
                <Input
                  {...register('CreditCardNumber', {
                    pattern: /\d\d\d\d \d\d\d\d \d\d\d\d \d\d\d\d/,
                    required: true,
                  })}
                  as={ReactInputMask}
                  mask="9999 9999 9999 9999"
                  placeholder="4242 1212 4242 1212"
                  maskChar={null}
                />
              </FormControl>

              <HStack>
                <FormControl isInvalid={!!errors?.Exp} minW={145}>
                  <FormLabel>{t("Date d'expiration")}</FormLabel>
                  <Input
                    {...register('Exp', {
                      pattern: /[0-1][0-9] \/ [0-9][0-9]/,
                      required: true,
                    })}
                    as={ReactInputMask}
                    placeholder="01 / 24"
                    mask="99 / 99"
                    maskChar={null}
                  />
                </FormControl>
                <FormControl isInvalid={!!errors?.Cryptogram}>
                  <FormLabel>{t('Cryptogramme')}</FormLabel>
                  <Input
                    {...register('Cryptogram', {
                      pattern: /[0-9][0-9][0-9]/,
                      required: true,
                    })}
                    as={ReactInputMask}
                    placeholder="123"
                    mask="9999"
                    maskChar={null}
                  />
                </FormControl>
              </HStack>
            </Stack>
            <FormControl isInvalid={!!errors.root}>
              <FormErrorMessage>{errors.root?.message}</FormErrorMessage>
            </FormControl>
            <ButtonGroup>
              <Button type="submit" isLoading={isSubmitting}>
                {t('Sauvegarder')}
              </Button>
              {hasPaymentMethodSetup && (
                <Button
                  onClick={() => {
                    reset();
                    onClose();
                  }}
                  colorScheme="red"
                >
                  {t('Annuler')}
                </Button>
              )}
            </ButtonGroup>
          </Stack>
        )}
      </Skeleton>
    </UserProfileCard>
  );
}

interface EditPasswordFormData {
  previousPassword: string;
  newPassword: string;
  confirmNewPassword: string;
}

function EditPassword() {
  const { t } = useTranslation();
  const toast = useToast();
  const formMethods = useForm<EditPasswordFormData>();
  const {
    handleSubmit,
    formState: { errors, isSubmitting, isValid },
    setError,
    reset,
  } = formMethods;

  async function onSubmit(data: EditPasswordFormData) {
    const { confirmNewPassword, newPassword, previousPassword } = data;
    if (newPassword !== confirmNewPassword) {
      return setError('confirmNewPassword', { message: t('Le mot de passe ne correspond pas') });
    }
    try {
      await HTTP.post('/auth/updatePassword', {
        previousPassword,
        newPassword,
      }).send();

      reset();

      toast({
        title: t('Votre mot de passe a bien été modifié.'),
        status: 'success',
      });
    } catch (e) {
      if (e instanceof ApiClientException) {
        if (e.ErrorCode === ApiClientExceptionErrorCode.InvalidLoginCredentials) {
          return setError('previousPassword', { message: 'Mot de passe invalide' });
        }

        setError('root', { message: e.getMessage() });
      }
    }
    return true;
  }

  const registerOptions = {
    minLength: { value: 6, message: t('Le mot de passe doit faire au moins 6 charactères') },
    required: t('Champ obligatoire'),
  };
  return (
    <UserProfileCard title={t(`Modifier mon mot de passe`)}>
      <FormProvider {...formMethods}>
        <Stack as="form" onSubmit={handleSubmit(onSubmit)} mt={10} alignItems="flex-start">
          <FloatingLabelInput
            label={t('Ancien mot de passe')}
            type="password"
            name="previousPassword"
            errorMessage={errors?.previousPassword?.message}
            registerOptions={registerOptions}
          />

          <FloatingLabelInput
            label={t('Nouveau mot de passe')}
            type="password"
            name="newPassword"
            errorMessage={errors?.newPassword?.message}
            registerOptions={registerOptions}
            marginTop={4}
          />

          <FloatingLabelInput
            label={t('Confirmer le mot de passe')}
            type="password"
            name="confirmNewPassword"
            errorMessage={errors?.confirmNewPassword?.message}
            registerOptions={registerOptions}
            marginTop={4}
          />

          <FormControl isInvalid={!!errors.root}>
            <FormErrorMessage mb={5}>{errors.root?.message}</FormErrorMessage>
          </FormControl>

          <Button type="submit" isLoading={isSubmitting} isDisabled={!isValid}>
            Confirmer
          </Button>
        </Stack>
      </FormProvider>
    </UserProfileCard>
  );
}
function UserProfileCard({ title, children, ...props }: { title: string; children: React.ReactNode } & BoxProps) {
  return (
    <Box className="accountBox" w="full" sx={{ padding: '1em' }} {...props}>
      <Heading textTransform="uppercase" className="accountSubTitle" size="md">
        {title}
      </Heading>
      {children}
    </Box>
  );
}

export default UserProfile;
